import { type FutureEventEntity } from '@solink/vd-data-layer/api-clients/platform/platform';
import { type DeviceId, type SiteId } from '@solinkcorp/api-types';
import { useUserSession } from '@solinkcorp/authentication';
import { toast } from '@solinkcorp/solink-components';
import { EVENT_TYPE_BOOKMARK } from '@solinkcorp/vd-common';
import EventActions from 'actions/EventActions';
import { CallhomeApi } from 'clients';
import { type ScheduledActivityEntity } from 'clients/CallhomeApiTypes';
import { type EventCamera } from 'clients/PlatformApi';
import clone from 'lodash/clone';
import merge from 'lodash/merge';
import AnalyticsLogger from 'models/AnalyticsLogger';
import { useCallback, useMemo, useState } from 'react';
import useFluxStore from 'src/hooks/useFluxStore';
import useLocale from 'src/hooks/useLocale';
import { LocationStore } from 'stores/LocationStore';
import * as DateHandler from 'utils/DateHandler';
import * as EventUtil from 'utils/EventUtil';
import { _debug } from 'utils/LoggingUtil';
import { getUnexecutedEvents, ifHasUnexecutedEvents } from 'utils/RecurrenceUtil';
import {
  EVENT_SUBTYPE_SCHEDULED,
  EXCLUDE_FROM_COUNT_TAG,
  HIDE_FROM_DISPLAY_TAG,
  STATUS_FAILED,
  STATUS_SUCCEEDED,
  type TypeOfAlarmException,
  VISIBILITY_PUBLIC,
} from 'vd-constants';

export type CancelScheduledEventType = ({
  event,
  updateEvent,
  successMessage,
}: {
  event: FutureEventEntity;
  updateEvent?: (newEvent: FutureEventEntity) => void;
  successMessage?: string;
}) => void;

type SaveScheduledEventType = ({
  event,
  newEventProps,
  showMessage,
  updateEvent,
  addEvent,
}: {
  event?: FutureEventEntity;
  newEventProps: ScheduledEventChangeProps;
  showMessage?: boolean;
  updateEvent?: (newEvent: FutureEventEntity) => void;
  addEvent?: (newEvent: FutureEventEntity) => void;
}) => void;

export type NewActivityType = Omit<ScheduledActivityEntity, 'id'>;

export interface ScheduledExceptionsBaseAnalytics {
  alarmExceptionStartTime: string;
  alarmExceptionEndTime: string;
  alarmExceptionRepeat: string;
  alarmExceptionTitle: string;
  alarmExceptionDescription: string;
  locationId: string;
  locationName: string;
  alarmExceptionType: TypeOfAlarmException;
}
export interface ScheduledExceptionsPreviousEventAnalytics {
  previousAlarmExeptionStartTime: string;
  previousAlarmExceptionEndTime: string;
  previousAlarmExceptionRepeat: string;
  previousAlarmExceptionTitle: string;
  previousAlarmExceptionDescription: string;
  previousAlarmExceptionType: TypeOfAlarmException;
}
interface SaveRecurringActivityParams {
  newActivity: NewActivityType;
  addActivity?: (activity: ScheduledActivityEntity) => void;
  showMessage?: boolean;
}

export type SaveRecurringActivityType = ({
  newActivity,
  addActivity,
  showMessage,
}: SaveRecurringActivityParams) => Promise<void>;

interface EditRecurringActivityParams {
  activity: ScheduledActivityEntity;
  newActivity: Partial<NewActivityType>;
  editActivity?: (activity: ScheduledActivityEntity) => void;
  showMessage?: boolean;
}

export type EditRecurringActivityType = (
  { activity, newActivity, editActivity, showMessage }: EditRecurringActivityParams,
  timezoneId: string,
) => Promise<void>;

interface DeleteRecurringActivityParams {
  activity: ScheduledActivityEntity;
  updateActivity?: (activity: ScheduledActivityEntity) => void;
  successMessage?: string;
  scheduledExceptionAnalytics: ScheduledExceptionsBaseAnalytics;
}

export type DeleteRecurringActivityType = (
  { activity, updateActivity, successMessage }: DeleteRecurringActivityParams,
  timezoneId: string,
) => void;

export interface ScheduledEventFormProps {
  title: string;
  isOpen: boolean;
  closeScheduledEventForm: () => void;
  saveRecurringActivity: SaveRecurringActivityType;
  editRecurringActivity: EditRecurringActivityType;
}

export interface ScheduledEventChangeProps {
  startTime: number | Date;
  endTime: number | Date;
  locationId: DeviceId;
  siteId: SiteId;
  cameras: EventCamera[];
  title: string;
  description?: string;
  reason: string;
  alarmEnable: boolean;
}

export function useScheduledEventForm(event?: FutureEventEntity): {
  currentEvent: FutureEventEntity | ScheduledActivityEntity | undefined;
  isLoading: boolean;
  cancelScheduledEvent: CancelScheduledEventType;
  reopenScheduledEvent: (updateEvent: (newEvent: FutureEventEntity) => void) => void;
  saveScheduledEvent: SaveScheduledEventType;
  saveRecurringActivity: SaveRecurringActivityType;
  editRecurringActivity: EditRecurringActivityType;
  deleteRecurringActivity: DeleteRecurringActivityType;
  openScheduledEventForm: () => void;
  scheduledEventFormProps: ScheduledEventFormProps | undefined;
  setCurrentEvent: (event?: FutureEventEntity | ScheduledActivityEntity) => void;
} {
  const locale = useLocale();
  const locationsById = useFluxStore(LocationStore, (store) => store.getLocationsById());
  const user = useUserSession();

  const [scheduledEventFormProps, setScheduledEventFormProps] = useState<
    ScheduledEventFormProps | undefined
  >(undefined);
  const [currentEvent, setCurrentEvent] = useState<
    FutureEventEntity | ScheduledActivityEntity | undefined
  >(event);
  const [isLoading, setIsLoading] = useState(false);
  const closeScheduledEventForm = useCallback(() => setScheduledEventFormProps(undefined), []);

  const finishSaving = useCallback(() => {
    setIsLoading(false);
    closeScheduledEventForm();
  }, [closeScheduledEventForm]);

  const saveScheduledEvent = useCallback(
    ({
      event,
      newEventProps,
      showMessage = true,
      updateEvent,
      addEvent,
    }: {
      event?: FutureEventEntity;
      newEventProps: ScheduledEventChangeProps;
      showMessage?: boolean;
      updateEvent?: (newEvent: FutureEventEntity) => void;
      addEvent?: (newEvent: FutureEventEntity) => void;
    }) => {
      const {
        startTime,
        endTime,
        locationId,
        siteId,
        cameras,
        title,
        description,
        reason,
        alarmEnable,
      } = newEventProps;

      const startTimeLocal = DateHandler.toUtcString(startTime);
      const endTimeLocal = DateHandler.toUtcString(endTime);
      const locationName = locationsById?.[siteId]?.name || 'Unknown';
      const primaryCameraId = cameras?.[0]?.id;
      const eventCameras: EventCamera[] = cameras.map((camera) => {
        const { chId, id, name, streams, annotations } = camera;
        const tenMinutesBefore = Date.now() - 60000;
        return {
          id,
          chId,
          name,
          annotations,
          snapshot_params: `time=${tenMinutesBefore}&precision=1&width=320`,
          thumbnail: `${locationId}/${id}-${tenMinutesBefore}.jpg`,
          streams: streams.map(({ id, name }) => {
            return { id, name };
          }),
        };
      });

      const scheduledEventBase = {
        subtype: EVENT_SUBTYPE_SCHEDULED,
        type: EVENT_TYPE_BOOKMARK,
        videos: [],
        scope: {
          visibility: VISIBILITY_PUBLIC,
        },
        tags: [HIDE_FROM_DISPLAY_TAG, EXCLUDE_FROM_COUNT_TAG],
        details: {
          externalId: 'future',
          scheduledActivity: {
            count: 1,
          },
        },
      };

      const theScheduledEventChangeProps = {
        cameras: eventCameras,
        startTimeLocal,
        endTimeLocal: DateHandler.toUtcString(endTime),
        locationId,
        subtitle: `Scheduled Ativity @ ${locationName}`,
        title,
        primaryCameraId,
        details: {
          scheduledActivity: {
            title,
            description,
            reason,
            alarmEnable,
            duration: Math.round(
              (new Date(endTime).getTime() - new Date(startTime).getTime()) / 1000,
            ), // in seconds
            period: {
              start: startTimeLocal,
              end: endTimeLocal,
            },
          },
        },
      };

      setIsLoading(true);

      if (event) {
        EventActions.editEvent(event.id, theScheduledEventChangeProps)
          .then((updatedEvent) => {
            finishSaving();
            if (updateEvent) {
              updateEvent(updatedEvent);
            }
            if (showMessage) {
              toast.success(locale.messages.updateSuccess);
            }
          })
          .catch(() => {
            setIsLoading(false);
            toast.error(locale.error.platformFail);
          });
      } else {
        const newEvent = clone(scheduledEventBase);
        merge(newEvent, theScheduledEventChangeProps);
        const callback = (error: any, createdEvent: FutureEventEntity) => {
          if (error) {
            setIsLoading(false);
            toast.error(locale.error.errorOccurred);
            return;
          }
          finishSaving();
          if (addEvent) {
            addEvent(createdEvent);
          }
          if (showMessage) {
            toast.success(locale.messages.createSuccess);
          }
        };

        if (!user.s3) {
          throw new Error('Missing s3');
        }

        const uploadThumbnail = false;
        EventActions.createEvent({ bookmark: newEvent }, callback, uploadThumbnail, user.s3);
      }
    },
    [locationsById, finishSaving, locale.messages, locale.error, user.s3],
  );

  const cancelScheduledEvent = useCallback(
    ({
      event,
      updateEvent,
      successMessage,
    }: {
      event: FutureEventEntity;
      updateEvent?: (newEvent: FutureEventEntity) => void;
      successMessage?: string;
    }) => {
      if (event) {
        const cancelProps = { state: 'CLOSED' };
        setIsLoading(true);
        EventActions.editEvent(event.id, cancelProps)
          .then((updatedEvent) => {
            setIsLoading(false);
            if (updateEvent) {
              updateEvent(updatedEvent);
            }
            if (successMessage) {
              toast.success(successMessage);
            }
          })
          .catch(() => {
            setIsLoading(false);
            toast.error(locale.error.platformFail);
          });
      } else {
        console.error('No scheduled event to cancel');
      }
    },
    [locale],
  );

  const reopenScheduledEvent = useCallback(
    (updateEvent: (newEvent: FutureEventEntity) => void) => {
      if (currentEvent) {
        const isInFuture = EventUtil.isInFuture(currentEvent as FutureEventEntity);
        const cancelProps = { state: 'OPEN' };
        setIsLoading(true);
        EventActions.editEvent(currentEvent.id, cancelProps)
          .then((updatedEvent) => {
            setIsLoading(false);
            updateEvent(updatedEvent);
            toast.success(
              isInFuture ? locale.event.scheduledReopend : locale.event.scheduledUnarchived,
            );
          })
          .catch(() => {
            setIsLoading(false);
            toast.error(locale.error.platformFail);
          });
      } else {
        console.error('No scheduled event to reopen or unarchive');
      }
    },
    [currentEvent, locale],
  );

  const saveRecurringActivity = useCallback(
    ({ newActivity, addActivity, showMessage = true }: SaveRecurringActivityParams) => {
      setIsLoading(true);
      return CallhomeApi()
        .createRecurringActivity(newActivity)
        .then((activity) => {
          finishSaving();
          if (addActivity) {
            addActivity(activity);
          }
          if (showMessage) {
            toast.success(locale.messages.createSuccess);
          }
        })
        .catch((error) => {
          setIsLoading(false);
          console.error(error);
          toast.error(locale.error.errorOccurred);
          throw error;
        });
    },
    [finishSaving, locale.error.errorOccurred, locale.messages.createSuccess],
  );

  const editRecurringActivity = useCallback(
    (
      { activity, newActivity, editActivity, showMessage = true }: EditRecurringActivityParams,
      timezoneId: string,
    ) => {
      setIsLoading(true);
      if (ifHasUnexecutedEvents(activity, timezoneId)) {
        deleteUnexcutedEvents(activity, timezoneId);
      }
      return CallhomeApi()
        .updateRecurringActivity(activity.id, newActivity)
        .then((updatedActivity) => {
          finishSaving();
          if (editActivity) {
            editActivity(updatedActivity);
          }
          if (showMessage) {
            toast.success(locale.messages.updateSuccess);
          }
        })
        .catch((error) => {
          setIsLoading(false);
          console.error(error);
          toast.error(locale.error.errorOccurred);
          throw error;
        });
    },
    [finishSaving, locale.error.errorOccurred, locale.messages.updateSuccess],
  );

  const deleteUnexcutedEvents = (
    params: {
      rule: string;
      reoccurringCount: number;
      durationSecs: number;
      deviceId?: string;
      id: string;
      startsAt?: number;
    },
    timezoneId: string,
  ) => {
    // Find the already created future event and delete them
    getUnexecutedEvents(params, timezoneId)
      .then((events) => {
        events.forEach((event) => {
          EventActions.editEvent(event.id, { state: 'CLOSED' })
            .then(() => {
              _debug(`Scheduled event ${event.id} has been canceled`);
            })
            .catch((error) => {
              console.error(error);
            });
        });
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const deleteRecurringActivity = useCallback(
    (
      {
        activity,
        updateActivity,
        successMessage,
        scheduledExceptionAnalytics,
      }: DeleteRecurringActivityParams,
      timezoneId: string,
    ) => {
      if (activity) {
        setIsLoading(true);
        if (ifHasUnexecutedEvents(activity, timezoneId)) {
          deleteUnexcutedEvents(activity, timezoneId);
        }
        CallhomeApi()
          .deleteRecurringActivity(activity.id)
          .then(() => {
            setIsLoading(false);
            if (updateActivity) {
              updateActivity(activity);
            }
            if (successMessage) {
              toast.success(successMessage);
            }
            AnalyticsLogger.alarmScheduledExceptionDelete({
              ...scheduledExceptionAnalytics,
              status: STATUS_SUCCEEDED,
            });
          })
          .catch(() => {
            setIsLoading(false);
            toast.error(locale.error.weEncounteredError);
            AnalyticsLogger.alarmScheduledExceptionDelete({
              ...scheduledExceptionAnalytics,
              status: STATUS_FAILED,
            });
          });
      } else {
        console.error('No recurring activity to delete');
      }
    },
    [locale.error.weEncounteredError],
  );

  const openScheduledEventForm = useCallback(() => {
    setScheduledEventFormProps({
      isOpen: true,
      title: currentEvent ? locale.event.editScheduled : locale.event.newScheduled,
      closeScheduledEventForm,
      saveRecurringActivity,
      editRecurringActivity,
    });
  }, [currentEvent, closeScheduledEventForm, saveRecurringActivity, editRecurringActivity, locale]);

  const scheduledEventController = useMemo(() => {
    return {
      currentEvent,
      isLoading,
      cancelScheduledEvent,
      reopenScheduledEvent,
      saveScheduledEvent,
      saveRecurringActivity,
      editRecurringActivity,
      deleteRecurringActivity,
      openScheduledEventForm,
      scheduledEventFormProps,
      setCurrentEvent,
    };
  }, [
    cancelScheduledEvent,
    currentEvent,
    isLoading,
    openScheduledEventForm,
    saveScheduledEvent,
    saveRecurringActivity,
    editRecurringActivity,
    deleteRecurringActivity,
    reopenScheduledEvent,
    scheduledEventFormProps,
  ]);

  return scheduledEventController;
}
